
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MarketplaceStrategy } from "@/types/strategy-types";
import { toast } from "sonner";
import { AccountPerformanceData } from "@/components/marketplace/types/strategy-card-types";

interface FollowedStrategiesLoaderProps {
  userId: string | undefined;
  isUserLoading: boolean;
  onStrategiesLoaded: (strategies: MarketplaceStrategy[]) => void;
}

const FollowedStrategiesLoader = ({ 
  userId, 
  isUserLoading, 
  onStrategiesLoaded 
}: FollowedStrategiesLoaderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const fetchFollowedStrategies = async () => {
      // Skip if already loaded or if userId is missing
      if (hasLoaded || !userId) {
        return;
      }

      try {
        setIsLoading(true);
        
        console.log("Fetching followed strategies for user:", userId);

        // First, get the followed strategies with basic strategy information
        const { data: followedData, error: followedError } = await supabase
          .from('followed_strategies')
          .select(`
            id,
            followed_at,
            strategy_id,
            strategy(
              id,
              name,
              min_investment,
              risk_level,
              exchange,
              expected_monthly_profit,
              plan,
              description,
              active,
              defined_max_drawdown,
              currency
            )
          `)
          .eq('user_id', userId)
          .order('followed_at', { ascending: false });

        if (followedError) {
          console.error('Error fetching followed strategies:', followedError);
          toast.error('Failed to load your strategies');
          setIsLoading(false);
          return;
        }

        // Transform the basic strategy data
        const strategies: MarketplaceStrategy[] = followedData
          .filter(item => item.strategy) // Filter out any null strategies
          .map((item: any) => ({
            id: item.id, // Using the follow record ID for unfollow functionality
            name: item.strategy?.name || "Untitled Strategy",
            strategy_name: item.strategy?.name || "Untitled Strategy",
            min_investment: item.strategy?.min_investment || 0,
            risk_level: item.strategy?.risk_level || 1,
            exchange: item.strategy?.exchange || "Unknown",
            followers_count: 0, // Initialize with default value
            performance_percentage: item.strategy?.expected_monthly_profit || 0,
            performance_since: new Date().toISOString().split('T')[0], // Default to today
            is_pro: item.strategy?.plan === 'premium' || item.strategy?.plan === 'pro',
            plan: item.strategy?.plan || 'free', // Add the actual plan value
            description: item.strategy?.description || "Trading strategy",
            is_active: item.strategy?.active || true,
            win_rate: null,
            max_drawdown: item.strategy?.defined_max_drawdown || null,
            trades_won: null,
            trades_lost: null,
            followed_at: item.followed_at, // Save the followed date
            strategy_id: item.strategy_id, // Include the strategy_id from the followed_strategies record
            currency: item.strategy?.currency || 'USDT' // Include the currency
          }));

        // Get strategy IDs to fetch performance data
        const strategyIds = followedData
          .filter(item => item.strategy)
          .map(item => item.strategy_id);

        if (strategyIds.length > 0) {
          // Get primary accounts for these strategies
          const { data: accountsData, error: accountsError } = await supabase
            .from('account')
            .select('id, strategy_id')
            .in('strategy_id', strategyIds)
            .eq('is_primary', true);

          if (accountsError) {
            console.error('Error fetching primary accounts:', accountsError);
          } else if (accountsData && accountsData.length > 0) {
            // Map strategy_id to account_id
            const strategyToAccountMap: Record<string, string> = {};
            accountsData.forEach(account => {
              strategyToAccountMap[account.strategy_id] = account.id;
            });

            // Get the account IDs to fetch performance data
            const accountIds = accountsData.map(a => a.id);

            if (accountIds.length > 0) {
              // Fetch the latest performance data for these accounts
              const { data: performanceData, error: performanceError } = await supabase
                .from('account_performance')
                .select('*')
                .in('account_id', accountIds)
                .order('timestamp', { ascending: false });

              if (performanceError) {
                console.error('Error fetching performance data:', performanceError);
              } else if (performanceData && performanceData.length > 0) {
                // Find the latest performance for each account
                const latestPerformanceByAccount: Record<string, AccountPerformanceData> = {};
                
                // Group by account_id and take the latest entry for each
                performanceData.forEach(perf => {
                  const accountId = perf.account_id;
                  if (!latestPerformanceByAccount[accountId] || 
                      new Date(perf.timestamp) > new Date(latestPerformanceByAccount[accountId].timestamp)) {
                    latestPerformanceByAccount[accountId] = perf as AccountPerformanceData;
                  }
                });

                // Update strategies with performance data
                strategies.forEach(strategy => {
                  const accountId = strategyToAccountMap[strategy.strategy_id || ''];
                  if (accountId && latestPerformanceByAccount[accountId]) {
                    const perfData = latestPerformanceByAccount[accountId];
                    strategy.win_rate = perfData.win_rate;
                    strategy.max_drawdown = perfData.max_drawdown;
                    strategy.trades_won = perfData.num_wins;
                    strategy.trades_lost = perfData.num_losses;
                    strategy.followers_count = perfData.num_followers || 0;
                    // Use pnl_ratio for performance if available
                    if (perfData.pnl_ratio !== null) {
                      strategy.performance_percentage = perfData.pnl_ratio;
                    }
                  }
                });
              }
            }
          }
        }

        console.log('Final strategies with performance data:', strategies);
        onStrategiesLoaded(strategies);
        setHasLoaded(true);
      } catch (error) {
        console.error('Error in fetchFollowedStrategies:', error);
        toast.error('Something went wrong while loading your strategies');
      } finally {
        setIsLoading(false);
      }
    };

    if (!isUserLoading && userId && !hasLoaded) {
      fetchFollowedStrategies();
    }
  }, [userId, isUserLoading, onStrategiesLoaded, hasLoaded]);

  // Return null instead of an object to make it a valid React component
  return null;
}

export default FollowedStrategiesLoader;
